import { useSyncedVideoTimeline, type VideoTimelineSnapshot } from "@carbon6/c6-frontend-design";
import { useLayoutEffect, useMemo, useRef } from "react";
import type { InspectChannel, InspectVideo } from "../domain/episodeInspect";
import { inspectVideoSource } from "../feature/videoTimeMapping";

/** 화면이 닫혀도 세션이 보존하는 위치·배속·재생 의도. 시계와 미디어 자원은 c6가 소유한다. */
export type InspectPlaybackMemory = {
  playback: Pick<VideoTimelineSnapshot, "currentTimeSeconds" | "playbackRate" | "playing">;
};

const NO_VIDEOS: readonly InspectVideo[] = [];
const NO_CHANNELS: readonly InspectChannel[] = [];

export function useInspectPlayback({
  videos = NO_VIDEOS,
  channels = NO_CHANNELS,
  durationSeconds,
  active,
  memory,
}: {
  videos?: readonly InspectVideo[];
  channels?: readonly InspectChannel[];
  durationSeconds: number;
  active: boolean;
  memory?: InspectPlaybackMemory;
}) {
  const localMemory = useRef<InspectPlaybackMemory>({
    playback: { currentTimeSeconds: 0, playbackRate: 1, playing: false },
  });
  const owner = memory ?? localMemory.current;
  const sources = useMemo(() => {
    const byKey = new Map(channels.map((channel) => [channel.key, channel]));
    return videos.map((video) => inspectVideoSource(video, byKey.get(video.key)));
  }, [channels, videos]);
  const timeline = useSyncedVideoTimeline({
    channels: sources,
    initialState: owner.playback,
    durationOverrideSeconds: durationSeconds,
    emitIntervalMs: 50,
    waitForBuffer: true,
  });

  // 최소화·섹션 이동은 위치와 재생 의도를 남긴다. 복귀한 화면만 공통 시계를 다시 돌린다.
  useLayoutEffect(() => {
    if (active && owner.playback.playing) timeline.play();
    return () => {
      const playing = active ? timeline.getSnapshot().playing : owner.playback.playing;
      timeline.pause();
      const { currentTimeSeconds, playbackRate } = timeline.getSnapshot();
      owner.playback = { currentTimeSeconds, playbackRate, playing };
    };
  }, [active, owner, timeline]);
  return timeline;
}
